import type {
  AdministrativeAreaOption,
  ExchangeLocationAssignment,
  ExchangeLocationType,
  OptionValue,
} from "./types";

const isSameValue = (firstValue: OptionValue, secondValue: OptionValue) =>
  String(firstValue) === String(secondValue);

export const sortExchangeLocationTypes = (types: ExchangeLocationType[]) =>
  [...types].sort((firstType, secondType) =>
    (firstType.name ?? firstType.label ?? "").localeCompare(
      secondType.name ?? secondType.label ?? ""
    )
  );

export const getExchangeLocationTypeLabel = (type: ExchangeLocationType) =>
  type.name ?? type.label ?? String(type.id);

export const findAdministrativeAreaLabel = (
  administrativeAreas: AdministrativeAreaOption[],
  locationId?: OptionValue
) => {
  if (locationId === undefined || locationId === null) {
    return "Unavailable";
  }

  const stack = [...administrativeAreas];
  while (stack.length) {
    const area = stack.shift();
    if (!area) {
      continue;
    }

    if (isSameValue(area.value, locationId)) {
      return area.label;
    }

    if (area.children?.length) {
      stack.push(...area.children);
    }
  }

  return "Unavailable";
};

export const isExchangeLocationTypeSelected = (
  assignments: ExchangeLocationAssignment[],
  exchangeLocationId: OptionValue,
  typeId: OptionValue
) =>
  assignments.some(
    assignment =>
      isSameValue(assignment.id, exchangeLocationId) &&
      assignment.list.some(selectedTypeId => isSameValue(selectedTypeId, typeId))
  );

export const toggleExchangeLocationTypeAssignment = (
  assignments: ExchangeLocationAssignment[],
  exchangeLocationId: OptionValue,
  typeId: OptionValue
) => {
  const existingAssignment = assignments.find(assignment =>
    isSameValue(assignment.id, exchangeLocationId)
  );

  if (!existingAssignment) {
    return [...assignments, { id: exchangeLocationId, list: [typeId] }];
  }

  return assignments.reduce<ExchangeLocationAssignment[]>((nextAssignments, assignment) => {
    if (!isSameValue(assignment.id, exchangeLocationId)) {
      return [...nextAssignments, assignment];
    }

    const hasType = assignment.list.some(selectedTypeId => isSameValue(selectedTypeId, typeId));
    const nextList = hasType
      ? assignment.list.filter(selectedTypeId => !isSameValue(selectedTypeId, typeId))
      : [...assignment.list, typeId];

    return nextList.length
      ? [...nextAssignments, { ...assignment, list: nextList }]
      : nextAssignments;
  }, []);
};
